import React, { FC } from 'react';

import { IImageData } from '../../../pages/Root/services/api/interfaces.ts';
import AsyncImage from '../AsyncImage/AsyncImage.tsx';
import TextParser from '../../../pages/Root/components/TextParser/TextParser.tsx';
import PublisherLogo from '../PublisherLogo/PublisherLogo.tsx';
import { StorySize } from '../../types/story-types.ts'
import { getDefaultDate } from '../../utils/time-util.ts';
import { useLocalizedDate } from '../../hooks/useLocalizedDate.ts';
import { useTranslation } from 'react-i18next';

import './StoryTop.scss';

export interface StoryTopProps {
  imageList: IImageData[];
  title: string;
  description: string;
  onClick?: () => void;
  publisherLogo?: string;
  publisherName?: string;
  darkPublisherLogo?: string;
  date?: string; // Optional date for the briefing
  briefingTitle?: string; // Optional briefing title like "Your Morning Briefing"
  useLocalizedDates?: boolean; // Optional flag to enable localized date formatting
}

const StoryTop: FC<StoryTopProps> = ({
  imageList,
  title,
  description,
  onClick,
  publisherLogo,
  publisherName,
  darkPublisherLogo,
  date,
  briefingTitle,
  useLocalizedDates = true
}: StoryTopProps) => {
  const { getDefaultDate: getLocalizedDefaultDate } = useLocalizedDate();
  const { t } = useTranslation();

  const displayDate = date || (useLocalizedDates ? getLocalizedDefaultDate() : getDefaultDate());
  const displayBriefingTitle = briefingTitle || t('yourDailyBriefing');
  // Get the first image from the imageList for AsyncImage
  const imageSrc = imageList && imageList.length > 0 ? imageList[0].link || '' : '';

  return (
    <div onClick={onClick} className="story-top">
      <div className="story-top__header">
        <div className="story-top__date title-lg">{displayDate}</div>
        <div className="story-top__briefing-title body-md">{displayBriefingTitle}</div>
      </div>

      <div className="story-top__image-container border-radius-lg">
        <AsyncImage
          src={imageSrc}
          alt={title}
          containerClassName="story-top__image"
          width="100%"
          height="100%"
        />
      </div>

      <div className="story-top__content">
        {publisherLogo && (
          <div className="story-top__publisher">
            <PublisherLogo
              publisherLogo={publisherLogo}
              publisherName={publisherName ?? ''}
              darkPublisherLogo={darkPublisherLogo}
              storySize={StorySize.LARGE}
            />
          </div>
        )}

        <div className="story-top__headline">
          {title && <TextParser
            text={title}
            option={{
              paragraphClass: 'story-top__title title-md',
            }}
          />}
        </div>

        <div className="story-top__description body-sm">
          {description && <TextParser
            text={description}
            option={{isIgnoreLinks: true, removeHTML: true}}
          />}
        </div>
      </div>
    </div>
  );
};

export default StoryTop;
